import { CheckCircle, Menu } from 'lucide-react'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { FeedbackForm } from '@/components/learn/FeedbackForm'
import { LessonSidebar } from '@/components/learn/LessonSidebar'
import { QuestionPanel } from '@/components/learn/QuestionPanel'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { VideoPlayer } from '@/components/video/VideoPlayer'
import { useCourseLearnData } from '@/hooks/use-learn'
import { useCompleteLesson, useRecordProgress } from '@/hooks/use-progress'

export default function LearnPage() {
  const { courseId } = useParams<{ courseId: string }>()
  const { data, isLoading } = useCourseLearnData(courseId!)
  const recordProgress = useRecordProgress()
  const completeLesson = useCompleteLesson()
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0)
  const [sidebarOpen, setSidebarOpen] = useState(false)

  if (isLoading) {
    return (
      <div className="flex h-screen">
        <Skeleton className="h-full w-64" />
        <Skeleton className="h-full flex-1" />
      </div>
    )
  }

  const learnData = data as any
  const lessons = learnData?.course?.lessons ?? []
  const currentLesson = lessons[currentLessonIndex]

  if (!currentLesson) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-muted-foreground">レッスンが見つかりません。</p>
      </div>
    )
  }

  const completedLessonIds: string[] = learnData?.completedLessonIds ?? []
  const isCurrentCompleted = completedLessonIds.includes(currentLesson.id)
  const isNonVideoLesson = currentLesson.type !== 'video'

  const handleProgress = (playedSecondsChunk: number, isNewPlayEvent: boolean) => {
    recordProgress.mutate({
      lessonId: currentLesson.id,
      data: { playedSecondsChunk, isNewPlayEvent },
    })
  }

  const handleComplete = () => {
    completeLesson.mutate(currentLesson.id, {
      onSuccess: () => toast.success('レッスンを完了しました'),
      onError: () => toast.error('完了処理に失敗しました'),
    })
  }

  const completionBadge = isCurrentCompleted ? (
    <span className="flex items-center gap-1 text-sm text-green-600">
      <CheckCircle className="h-5 w-5" />
      完了済み
    </span>
  ) : isNonVideoLesson ? (
    <Button size="sm" onClick={handleComplete} disabled={completeLesson.isPending}>
      {completeLesson.isPending ? '処理中...' : '完了にする'}
    </Button>
  ) : null

  return (
    /* モバイル: 自然な縦スクロール / デスクトップ: 固定高さ3カラム */
    <div className="flex flex-col md:h-[calc(100vh-64px)] md:flex-row">
      {/* ── デスクトップ サイドバー（md以上で表示） ── */}
      <div className="hidden md:block">
        <LessonSidebar
          lessons={lessons}
          currentIndex={currentLessonIndex}
          onSelect={setCurrentLessonIndex}
          courseTitle={learnData?.course?.title}
        />
      </div>

      {/* ── モバイル ツールバー（md未満で表示） ── */}
      <div className="flex items-center gap-2 border-b bg-white px-3 py-2 md:hidden">
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="sm" className="shrink-0 gap-1.5 px-2">
              <Menu className="h-5 w-5" />
              <span className="text-sm">レッスン</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0">
            <LessonSidebar
              lessons={lessons}
              currentIndex={currentLessonIndex}
              onSelect={(i) => {
                setCurrentLessonIndex(i)
                setSidebarOpen(false)
              }}
              courseTitle={learnData?.course?.title}
            />
          </SheetContent>
        </Sheet>

        <span className="flex-1 truncate text-sm font-medium">{currentLesson.title}</span>
        {completionBadge}
      </div>

      {/* ── メインコンテンツ ── */}
      <div className="flex flex-1 flex-col md:flex-row md:overflow-hidden">
        {/* 動画エリア */}
        <div className="flex flex-col md:flex-1 md:overflow-hidden">
          <div className="aspect-video w-full bg-black">
            <VideoPlayer lessonId={currentLesson.id} onProgressUpdate={handleProgress} />
          </div>

          {/* タイトル行（デスクトップのみ動画下に表示） */}
          <div className="hidden shrink-0 items-center justify-between border-t px-4 py-3 md:flex">
            <h2 className="text-xl font-bold">{currentLesson.title}</h2>
            {completionBadge}
          </div>
        </div>

        {/* タブパネル（モバイル: 動画下・全幅 / デスクトップ: 右サイド w-80） */}
        <div className="flex flex-col border-t md:w-80 md:shrink-0 md:border-l md:border-t-0 md:overflow-hidden">
          <Tabs defaultValue="questions" className="flex flex-1 flex-col md:overflow-hidden">
            <TabsList className="m-3 w-[calc(100%-1.5rem)] shrink-0">
              <TabsTrigger value="questions" className="flex-1">
                Q&A
              </TabsTrigger>
              <TabsTrigger value="feedback" className="flex-1">
                評価
              </TabsTrigger>
            </TabsList>
            <TabsContent value="questions" className="px-3 pb-6 md:flex-1 md:overflow-auto md:pb-3">
              <QuestionPanel lessonId={currentLesson.id} />
            </TabsContent>
            <TabsContent value="feedback" className="px-3 pb-6 md:flex-1 md:overflow-auto md:pb-3">
              <FeedbackForm
                key={currentLesson.id}
                lessonId={currentLesson.id}
                evaluationPrompt={learnData?.course?.evaluationPrompt}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  )
}
